// Accounts, people, the day's alerts and notes — part of the API surface; views import from '../api' (index.js), never from here.
import { get, post, put, del, postForm } from './http';

export const authApi = {
  login: (email, password) => post('/auth/login', { email, password }),
  logout: () => post('/auth/logout'),
  me: () => get('/auth/me'),
  // The caller's own light/dark choice; null = follow the device (migration 066).
  setTheme: (theme) => put('/auth/me/theme', { theme }),
  // ⚠ THE MONEY IDENTITIES, not the people. This is who may ISSUE AN INVOICE, PAY AN
  // EXPENSE or HOLD A PROFIT SHARE — the `partners` table, which is a strict subset of
  // the users below. A picker that attributes money reads this one; a picker that names
  // a person reads `usersApi`. Substituting one for the other is how a secretary ends up
  // in an issuer dropdown.
  partners: () => get('/auth/partners'),
};

// THE PEOPLE — everyone who can log in, which since the role model is a wider set than
// the partners above. Read-only: creating a user and setting a role belong to the
// management screen, which ships with the role enforcement it depends on.
//
// Three scopes over one list, because the three pickers ask different questions:
//   list()          everyone
//   assignable()    who may be given a task or booked into a meeting — a content writer
//                   is excluded, since they see only their upload screen and would never
//                   learn of the work
//   fronters()      who may be named as having fronted a cost out of their own pocket.
//                   PARTNERS ARE EXCLUDED, and that is the important one: a partner who
//                   pays out of pocket records a PAID expense with themselves as payer,
//                   which is exactly what the standing gap already settles. See
//                   server/api/rest/users/service.js.
export const usersApi = {
  list: () => get('/users'),
  assignable: () => get('/users?for=assignable'),
  fronters: () => get('/users?for=fronters'),
};

// USER MANAGEMENT — a different endpoint from the lookup above, because it is a
// different permission. The reads there are open to any member of staff; everything here
// is partner-only, enforced on its own mount in the server's route table rather than by a
// check inside a shared router.
//
// No `me` and no self-service: a person cannot change their own role or reactivate
// themselves, because every one of these goes through the partner-only mount.
export const usersAdminApi = {
  list: () => get('/admin/users'),
  create: (body) => post('/admin/users', body),
  // Name, email, role and colour. Never the password — that is its own act below, so a
  // form that edits a name cannot reset credentials by accident.
  update: (id, body) => put(`/admin/users/${id}`, body),
  setPassword: (id, password) => put(`/admin/users/${id}/password`, { password }),
  // Deactivating ends every session the user holds, in the same request. The response
  // says how many died so the screen can report it.
  setActive: (id, isActive) => put(`/admin/users/${id}/active`, { is_active: isActive }),
  // Permanent, and usually refused: every audit stamp naming the user holds them. The
  // ordinary act for somebody who has left is deactivation.
  remove: (id) => del(`/admin/users/${id}`),

  // WHAT A CONTENT WRITER MAY SEE. `grants` is what was granted; `grantsPreview` is what
  // those grants actually REACH once a retainer carries its topics — and it is the same
  // server function the writer's own screen calls, so the partner is shown exactly what
  // they are handing over rather than a second opinion about it.
  grants: (id) => get(`/admin/users/${id}/grants`),
  grantsPreview: (id) => get(`/admin/users/${id}/grants/preview`),
  setGrants: (id, retainerIds, jobIds) =>
    put(`/admin/users/${id}/grants`, { retainer_ids: retainerIds, job_ids: jobIds }),
};

// THE CONTENT WRITER'S ONE SURFACE — three calls, and the absence of a fourth IS the
// design.
//
// ⚠ THERE IS NO DOWNLOAD HERE because the server mounts none on that router. A presigned
// URL needs no further authentication once minted and can be forwarded to anybody, so the
// protection is not a check that refuses a writer — it is that a writer has nothing to
// ask. The function that mints them lives on the STAFF `/jobs` router.
//
// `scope` is names only: retainer → its topics, plus standalone granted jobs. No amounts,
// no profit, no other client. The server builds it from the grant tables, and
// `npm run check:grants` asserts the payload field by field against an allowlist.
//
// ⚠ AND IT IS NOT THE AUTHORITY. Whatever this client holds, the server re-reads the
// grant tables on every upload — a job id that was never granted is refused, and a grant
// revoked a moment ago refuses an upload a moment later.
export const uploadsApi = {
  scope: () => get('/uploads/scope'),
  // What this user has already sent for one job — their own uploads, not the office's
  // filing on that job.
  mine: (jobId) => get(`/uploads/jobs/${jobId}/files`),
  upload: (jobId, formData) => postForm(`/uploads/jobs/${jobId}/files`, formData),
};

export const dashboardApi = {
  // period: { mode: 'month' | 'all', month?: 'YYYY-MM' }. Only the period figures
  // react to it; the snapshot ones are always "as of today".
  summary: (period = {}) => {
    const q = new URLSearchParams();
    if (period.mode === 'all') q.set('mode', 'all');
    else if (period.month) q.set('month', period.month);
    const qs = q.toString();
    return get(`/dashboard${qs ? `?${qs}` : ''}`);
  },
};

// The notification surface — ONE call behind both the bell and the sidebar dots. There
// is deliberately no second endpoint for the dots: two reads of the same fact would
// eventually disagree on screen (see server/api/rest/alerts/service.js).
export const alertsApi = {
  get: () => get('/alerts'),
};

// A lead's contact history — one row per conversation, accumulating. Its own resource,
// not a branch of clients: `clients.notes` is the standing general-info field and is
// written through the client/lead form, while these are the record of what was said.
export const clientNotesApi = {
  byClient: (clientId) => get(`/client-notes?client_id=${clientId}`),
  create: (body) => post('/client-notes', body),
  update: (id, body) => put(`/client-notes/${id}`, body),
  // Permanent, and takes nothing with it: a note is a single leaf row.
  remove: (id) => del(`/client-notes/${id}`),
};

// THE PINBOARD — and NOT the neighbour above, which it is one letter away from in every
// import line. `clientNotesApi` is a lead's contact history; this one is a note a person
// left for a person and knows nothing about a client.
//
// `list()` takes no scope: the board is shared, every member of staff sees every note,
// and the שלי / של כולם toggle is a view the browser takes over this one list — the same
// shape the clients and retainers screens use. A second, narrower endpoint would be a
// second chance for the two halves to disagree.
export const notesApi = {
  list: () => get('/notes'),
  // `for_user_id` is the one thing the form chooses; omitting it means "for myself". The
  // author is stamped from the session and is never sent.
  create: (body) => post('/notes', body),
  // Title and body only — the server ignores anything else, since re-addressing a note
  // is a different note rather than a correction. Author only: somebody else's note
  // answers 403, and the screen does not offer the button in the first place.
  update: (id, body) => put(`/notes/${id}`, body),
  remove: (id) => del(`/notes/${id}`),
};
